import React, {
  useState,
  type FormEvent,
  useRef,
  useEffect,
  type KeyboardEvent,
  useCallback,
  useMemo,
} from "react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Search,
  ArrowRight,
  Building2,
  DollarSign,
  Maximize2,
  X,
  Users,
  Info,
  Filter,
  ChevronDownIcon,
  MapPinIcon,
} from "lucide-react";
import { FilterValues } from "../types";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  PainelFiltersList,
  type Filters as PainelFilters,
} from "./painel-filters";
import { Button } from "@/components/ui/button";
import { getDefaultPropertyType } from "../constants/property-types";
import { SearchAutocomplete } from "@/components/inputs/search-autocomplete/search-autocomplete";
import { SearchInputCommand } from "@/components/inputs/search-input-command";
import { useFilters } from "@/hooks/use-filters";
import { useSearchSpaces } from "@/hooks/useSearchSpaces";
import { spaceService } from "@/services/space-service";
import { MagnifyingGlassIcon } from "@heroicons/react/24/outline";
import { SPACE_TYPE } from "@/constants/space-type";
import { sendTrackDataServer } from "@/features/analytics/utils/serverTrackData";
import { useAuth } from "@/features/authentication";

const DEFAULT_DISTANCE = 30;

type SearchVision360Props = {
  defaultFilters?: FilterValues;
  onSearch: (filters: FilterValues) => void;
  onPainelFiltersOpenChange?: (isOpen: boolean) => void;
  dealId?: string | null;
};

export default function SearchVision360({
  defaultFilters,
  onSearch,
  onPainelFiltersOpenChange,
  dealId,
}: SearchVision360Props) {
  const { userInfo } = useAuth();
  const [_, setSearchSelectedValue] = useState("");
  const [filters, setFilters] = useState<FilterValues>(
    defaultFilters || {
      query: "",
      propertyType: getDefaultPropertyType(),
      isFurnished: false,
      budgetMin: "",
      budgetMax: "",
      sizeMin: "",
      sizeMax: "",
      positionsMin: "",
      positionsMax: "",
      minPrice: undefined,
      maxPrice: undefined,
      minCapacity: undefined,
      maxCapacity: undefined,
      privateOfficeStatus: ["0"],
      amenitiesSlugs: [],
      amenitiesSlugsOptional: [],
      distance: DEFAULT_DISTANCE,
      unitCreditPrice: 80,
      capacityWeight: undefined,
      priceWeight: undefined,
      searchType: "location",
      location: undefined,
      latitude: undefined,
      longitude: undefined,
      spaceId: undefined,
      privateOfficeIds: undefined,
      cityName: undefined,
      neighborhood: undefined,
      skipRecentUpdatesFilter: false,
    },
  );

  const [painelFilters, setPainelFilters] = useState<PainelFilters>({
    minPrice: defaultFilters?.minPrice,
    maxPrice: defaultFilters?.maxPrice,
    minCapacity: defaultFilters?.minCapacity,
    maxCapacity: defaultFilters?.maxCapacity,
    privateOfficeStatus: defaultFilters?.privateOfficeStatus ?? ["0"],
    amenitiesSlugs: defaultFilters?.amenitiesSlugs ?? [],
    amenitiesSlugsOptional: defaultFilters?.amenitiesSlugsOptional ?? [],
    distance: defaultFilters?.distance ?? DEFAULT_DISTANCE,
    unitCreditPrice: defaultFilters?.unitCreditPrice ?? 80,
    capacityWeight: defaultFilters?.capacityWeight,
    priceWeight: defaultFilters?.priceWeight,
    skipRecentUpdatesFilter: defaultFilters?.skipRecentUpdatesFilter ?? false,
  });

  const [searchType, setSearchType] = useState<
    "location" | "space" | "privateOfficeIds" | "neighborhoods"
  >("location");
  const [selectedCity, setSelectedCity] = useState<{
    id: number;
    name: string;
  } | null>(null);
  const [selectedNeighborhoods, setSelectedNeighborhoods] = useState<
    { id: number; name: string }[]
  >([]);
  const [selectedSpace, setSelectedSpace] = useState<{
    id: number;
    name: string;
  } | null>(null);
  const [roomId, setRoomId] = useState("");

  const {
    handSelectedCity,
    filterCities,
    handSelectedNeighborhood,
    filterNeighborhoods,
    handSelectedSpace,
    filterSpaces,
    setNeighborhoods,
  } = useFilters();

  const {
    isLoading: isLoadingSpaces,
    spaces,
    handleSearchSpaces,
  } = useSearchSpaces();

  const searchOptions = useMemo(
    () => [
      { id: 1, name: "Localização", type: "location" as const },
      { id: 2, name: "Espaço", type: "space" as const },
      { id: 3, name: "ID da sala", type: "privateOfficeIds" as const },
      { id: 4, name: "Múltiplos bairros", type: "neighborhoods" as const },
    ],
    [],
  );

  const loadSelectedSpace = async (spaceId: number) => {
    try {
      const space = await spaceService.getSpaceById({ spaceId });
      setSelectedSpace({
        id: space.id,
        name: space.name,
      });
    } catch (error) {
      console.error("Erro ao carregar espaço:", error);
    }
  };

  const resetAllLocationFields = () => {
    setSelectedCity(null);
    setSelectedNeighborhoods([]);
    setSelectedSpace(null);
    setRoomId("");
    setFilters((prev) => ({
      ...prev,
      location: undefined,
      latitude: undefined,
      longitude: undefined,
      spaceId: undefined,
      privateOfficeIds: undefined,
      cityName: undefined,
      neighborhood: undefined,
    }));
  };

  const clearIrrelevantLocationFields = (
    type: "location" | "space" | "privateOfficeIds" | "neighborhoods",
  ) => {
    if (type !== "space") {
      setFilters((prev) => ({ ...prev, spaceId: undefined }));
    }
    if (type !== "privateOfficeIds") {
      setFilters((prev) => ({ ...prev, privateOfficeIds: undefined }));
    }
    if (type !== "neighborhoods") {
      setFilters((prev) => ({
        ...prev,
        cityName: undefined,
        neighborhood: undefined,
      }));
    }
    if (type !== "location") {
      setFilters((prev) => ({
        ...prev,
        location: undefined,
        latitude: undefined,
        longitude: undefined,
      }));
    }
  };

  const handleCitySelect = (item: { id: number; name: string }) => {
    clearIrrelevantLocationFields("neighborhoods");
    setSelectedCity(item);
    setNeighborhoods(undefined);
    setSelectedNeighborhoods([]);
    setFilters((prev) => ({ ...prev, cityName: item.name }));
  };

  const handleNeighborhoodSelect = (item: { id: number; name: string }) => {
    clearIrrelevantLocationFields("neighborhoods");
    setSelectedNeighborhoods((prev) => {
      const exists = prev.some((n) => n.id === item.id);
      const updated = exists
        ? prev.filter((n) => n.id !== item.id)
        : [...prev, item];

      setFilters((prev) => ({
        ...prev,
        neighborhood: updated.map((n) => n.name),
      }));
      return updated;
    });
  };

  const [isFocused, setIsFocused] = useState(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const searchAndTrackTimeoutRef = useRef<NodeJS.Timeout>();

  useEffect(() => {
    if (defaultFilters) {
      setFilters(defaultFilters);
      setPainelFilters({
        minPrice: defaultFilters?.minPrice,
        maxPrice: defaultFilters?.maxPrice,
        minCapacity: defaultFilters?.minCapacity,
        maxCapacity: defaultFilters?.maxCapacity,
        privateOfficeStatus: defaultFilters?.privateOfficeStatus,
        amenitiesSlugs: defaultFilters?.amenitiesSlugs ?? [],
        amenitiesSlugsOptional: defaultFilters?.amenitiesSlugsOptional ?? [],
        distance: defaultFilters?.distance ?? DEFAULT_DISTANCE,
        unitCreditPrice: defaultFilters?.unitCreditPrice ?? 80,
        capacityWeight: defaultFilters?.capacityWeight,
        priceWeight: defaultFilters?.priceWeight,
        skipRecentUpdatesFilter:
          defaultFilters?.skipRecentUpdatesFilter ?? false,
      });

      if (defaultFilters.searchType) {
        setSearchType(defaultFilters.searchType);
      }

      if (defaultFilters.searchType === "space" && defaultFilters.spaceId) {
        setSelectedCity(null);
        setSelectedNeighborhoods([]);
        setRoomId("");
        loadSelectedSpace(defaultFilters.spaceId);
      } else if (
        defaultFilters.searchType === "privateOfficeIds" &&
        defaultFilters.privateOfficeIds
      ) {
        setSelectedCity(null);
        setSelectedNeighborhoods([]);
        setSelectedSpace(null);

        if (!roomId) {
          //apply just if room id is not set, by default room id is set on handleChangeRoomId
          setRoomId(defaultFilters.privateOfficeIds.join(", "));
        }
      } else if (defaultFilters.searchType === "neighborhoods") {
        if (defaultFilters.cityName) {
          setSelectedCity({ id: 0, name: defaultFilters.cityName });
        }
        if (defaultFilters.neighborhood) {
          setSelectedNeighborhoods(
            defaultFilters.neighborhood.map((name, index) => ({
              id: index,
              name,
            })),
          );
        }
        setSelectedSpace(null);
        setRoomId("");
      } else if (
        defaultFilters.searchType === "location" &&
        defaultFilters.location
      ) {
        setSelectedCity(null);
        setSelectedNeighborhoods([]);
        setSelectedSpace(null);
        setRoomId("");
        setSearchSelectedValue(defaultFilters.location);
      }
    }
  }, [defaultFilters]);

  useEffect(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = "auto";
      const newHeight = Math.max(textarea.scrollHeight, 36);
      textarea.style.height = `${newHeight}px`;
    }
  }, [filters.query]);

  useEffect(() => {
    onPainelFiltersOpenChange?.(isDrawerOpen);
  }, [isDrawerOpen, onPainelFiltersOpenChange]);

  useEffect(() => {
    return () => {
      if (searchAndTrackTimeoutRef.current) {
        clearTimeout(searchAndTrackTimeoutRef.current);
      }
    };
  }, []);

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();

    handleSearchAndTrack(filters);
  };

  const handleSearchAndTrack = (filters: FilterValues) => {
    sendTrackDataServer({
      eventName: "VISION_SEARCH_SUBMITTED",
      data: {
        deal_id: dealId ?? null,
        user_id: userInfo.user?.id ?? null,
        email: userInfo.profile?.email ?? null,
        search_type: filters.searchType,
        city_name: filters.cityName,
        neighborhood: filters.neighborhood,
        location: filters.location,
        latitude: filters.latitude,
        longitude: filters.longitude,
        space_id: filters.spaceId,
        private_office_ids: filters.privateOfficeIds,
        capacity_min: filters.minCapacity,
        capacity_max: filters.maxCapacity,
        capacity_weight: filters.capacityWeight,
        positions_min: filters.positionsMin,
        positions_max: filters.positionsMax,
        price_min: filters.minPrice,
        price_max: filters.maxPrice,
        price_weight: filters.priceWeight,
        unit_credit_price: filters.unitCreditPrice,
        budget_min: filters.budgetMin,
        budget_max: filters.budgetMax,
        query: filters.query,
        property_type: filters.propertyType,
        is_furnished: filters.isFurnished,
        size_min: filters.sizeMin,
        size_max: filters.sizeMax,
        distance: filters.distance,
        amenities: filters.amenitiesSlugs,
        amenities_optional: filters.amenitiesSlugsOptional,
        status: filters.privateOfficeStatus,
        skip_recent_updates_filter: filters.skipRecentUpdatesFilter,
      },
      url: location.pathname,
      productType: "PO",
    });
    onSearch(filters);
  };

  const updateFilter = (
    key: keyof FilterValues,
    value: string | number | boolean | "",
  ) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const updatePainelFilters = useCallback((newPainelFilters: PainelFilters) => {
    setPainelFilters(newPainelFilters);
    setFilters((prev) => {
      const updated = { ...prev, ...newPainelFilters };
      return updated;
    });
  }, []);

  const clearQuery = useCallback(() => {
    updateFilter("query", "");
    if (textareaRef.current) {
      textareaRef.current.focus();
    }
  }, []);

  const clearBudget = () => {
    updateFilter("budgetMin", "");
    updateFilter("budgetMax", "");
  };

  const clearSize = () => {
    updateFilter("sizeMin", "");
    updateFilter("sizeMax", "");
  };

  const clearPositions = () => {
    updateFilter("positionsMin", "");
    updateFilter("positionsMax", "");
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSearchAndTrack(filters);
    }
  };

  const toggleFurnished = () => {
    updateFilter("isFurnished", !filters.isFurnished);
  };

  const getBudgetRangeText = () => {
    if (filters.budgetMin && filters.budgetMax) {
      return `R$${filters.budgetMin} - R$${filters.budgetMax}`;
    } else if (filters.budgetMin) {
      return `De R$${filters.budgetMin}`;
    } else if (filters.budgetMax) {
      return `Até R$${filters.budgetMax}`;
    }
    return "Preço";
  };

  const getSizeRangeText = () => {
    if (filters.sizeMin && filters.sizeMax) {
      return `De ${filters.sizeMin} a ${filters.sizeMax} m²`;
    } else if (filters.sizeMin) {
      return `De ${filters.sizeMin} m²`;
    } else if (filters.sizeMax) {
      return `Até ${filters.sizeMax} m²`;
    }
    return "Tamanho";
  };

  const getPositionsRangeText = () => {
    if (filters.positionsMin && filters.positionsMax) {
      return `De ${filters.positionsMin} a ${filters.positionsMax} posições`;
    } else if (filters.positionsMin) {
      return `De ${filters.positionsMin} posições`;
    } else if (filters.positionsMax) {
      return `Até ${filters.positionsMax} posições`;
    }
    return "Posições";
  };

  const isActive = isFocused || filters.query.length > 0;
  const hasContent = filters.query.length > 0;
  const hasBudgetFilter = filters.budgetMin !== "" || filters.budgetMax !== "";
  const hasSizeFilter = filters.sizeMin !== "" || filters.sizeMax !== "";
  const hasPositionsFilter =
    filters.positionsMin !== "" || filters.positionsMax !== "";

  const hasPainelFilters = useCallback(() => {
    const defaultValues = {
      minPrice: undefined,
      maxPrice: undefined,
      minCapacity: undefined,
      maxCapacity: undefined,
      privateOfficeStatus: ["0"],
      amenitiesSlugs: [],
      amenitiesSlugsOptional: [],
      distance: DEFAULT_DISTANCE,
      unitCreditPrice: 80,
      capacityWeight: undefined,
      priceWeight: undefined,
      skipRecentUpdatesFilter: false,
    };

    const hasActiveFilters = Object.entries(painelFilters).some(
      ([key, value]) => {
        const defaultValue = defaultValues[key as keyof typeof defaultValues];

        if (Array.isArray(value)) {
          if (key === "privateOfficeStatus") {
            return !(value.length === 1 && value[0] === "0");
          }
          return value.length > 0;
        }

        return value !== defaultValue;
      },
    );

    return hasActiveFilters;
  }, [painelFilters]);

  const handleSearchLocation = async (data: any) => {
    if (!data) {
      setSearchSelectedValue("");
      setFilters((prev) => ({
        ...prev,
        location: undefined,
        latitude: undefined,
        longitude: undefined,
      }));
      handleSearchAndTrack({
        ...filters,
        location: undefined,
        latitude: undefined,
        longitude: undefined,
      });
      return;
    }

    const location = `${data?.name}${data?.address ? ` - ${data?.address}` : ""}`;

    setSearchSelectedValue(location);
    setFilters((prev) => ({ ...prev, location: location }));

    const space =
      location && location.length > 3
        ? await spaceService.getSpaceByLocation(location)
        : null;

    handleSearchAndTrack({
      ...filters,
      location: `${data?.name}${data?.address ? ` - ${data?.address}` : ""}`,
      latitude: space?.lat.toString(),
      longitude: space?.lng.toString(),
    });
  };

  const handleChangeRoomId = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setRoomId(value);

    const ids = value
      .trim()
      .split(",")
      .map((id) => id.trim())
      .filter((id) => id.length > 0);

    const nextFilters = {
      ...filters,
      spaceId: undefined,
      location: undefined,
      latitude: undefined,
      longitude: undefined,
      cityName: undefined,
      neighborhood: undefined,
      privateOfficeIds: ids,
    };

    setFilters(nextFilters);

    if (searchAndTrackTimeoutRef.current) {
      clearTimeout(searchAndTrackTimeoutRef.current);
    }

    searchAndTrackTimeoutRef.current = setTimeout(() => {
      handleSearchAndTrack(nextFilters);
    }, 300);
  };

  const HIDE_BTS_ITEMS = true;
  const HIDE_SEMANTIC_SEARCH = true;

  return (
    <div
      className={`w-full border-2 rounded-2xl overflow-hidden transition-all duration-300 ${
        isActive ? "border-gray-600 shadow-md" : "border-gray-200"
      }`}
    >
      <form onSubmit={handleSubmit}>
        <div className="relative">
          {!HIDE_SEMANTIC_SEARCH && (
            <div className="flex items-start px-4 py-3 min-h-[60px]">
              <Search
                className="h-5 w-5 text-gray-400 mr-2 mt-1.5 flex-shrink-0"
                aria-hidden="true"
              />
              <div className="relative flex-1">
                <textarea
                  ref={textareaRef}
                  value={filters.query}
                  onChange={(e) => updateFilter("query", e.target.value)}
                  onKeyDown={handleKeyDown}
                  onFocus={() => setIsFocused(true)}
                  onBlur={() => setIsFocused(false)}
                  placeholder="Busque por localização, características ou palavras-chave..."
                  className="w-full bg-transparent border-0 focus:ring-0 focus:outline-none focus:border-0 focus:shadow-none text-gray-800 placeholder-gray-400 resize-none overflow-hidden min-h-[24px] max-h-[120px] py-0 pr-10"
                  aria-label="Search query"
                  rows={1}
                />
                {hasContent && (
                  <button
                    type="button"
                    onClick={clearQuery}
                    className="absolute top-0 right-0 p-1 text-gray-400 hover:text-gray-600 transition-colors"
                    aria-label="Clear search"
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>
            </div>
          )}

          {!HIDE_SEMANTIC_SEARCH && (
            <button
              type="submit"
              className="absolute bottom-3 right-4 bg-gray-600 text-white p-2 rounded-lg hover:bg-gray-700 transition-colors"
              aria-label="Submit search"
            >
              <ArrowRight className="h-5 w-5" />
            </button>
          )}
        </div>

        <div
          className={`flex flex-wrap gap-2 p-3 bg-gray-50/50 border-gray-200 transition-all duration-300 items-center overflow-visible ${HIDE_SEMANTIC_SEARCH ? "" : "border-t"}`}
        >
          <div className="relative flex items-center overflow-visible">
            <div className="relative inline-flex items-center overflow-visible">
              <div
                className="flex items-center justify-center space-x-2 px-3 h-10 text-sm appearance-none bg-white"
                aria-label="Property type"
              >
                <Building2
                  className="h-4 w-4 text-gray-600"
                  aria-hidden="true"
                />
                <span className="text-gray-700">Private Office</span>
              </div>
            </div>
          </div>

          <div className="relative flex items-center overflow-visible">
            <div className="relative inline-flex items-center overflow-visible">
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    type="button"
                    variant="outline"
                    className="flex items-center justify-center space-x-2 px-3 h-10 text-sm border border-gray-300 rounded-full focus:ring-2 focus:ring-gray-500 focus:border-transparent appearance-none bg-white transition-all duration-300 cursor-pointer hover:border-gray-500"
                    aria-label="Search type"
                  >
                    <MapPinIcon
                      className="h-4 w-4 text-gray-600"
                      aria-hidden="true"
                    />
                    <span className="text-gray-700">
                      {searchOptions.find((opt) => opt.type === searchType)
                        ?.name || "Localização"}
                    </span>
                    <ChevronDownIcon className="h-4 w-4 text-gray-600" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent
                  className="w-80 bg-white shadow-lg rounded-lg p-4 border border-gray-200 z-[9999]"
                  align="start"
                  side="bottom"
                >
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <h3 className="font-medium text-gray-900">
                        Tipo de Busca
                      </h3>
                    </div>
                    <div className="flex flex-col">
                      {searchOptions.map((option) => (
                        <div
                          key={option.id}
                          className="flex items-center w-full"
                        >
                          <Checkbox
                            id={`searchType-${option.type}`}
                            checked={searchType === option.type}
                            onCheckedChange={(
                              checked: boolean | "indeterminate",
                            ) => {
                              if (checked === true) {
                                resetAllLocationFields();
                                const newSearchType = option.type as
                                  | "location"
                                  | "space"
                                  | "privateOfficeIds"
                                  | "neighborhoods";
                                setSearchType(newSearchType);
                                setFilters((prev) => ({
                                  ...prev,
                                  searchType: newSearchType,
                                }));
                              }
                            }}
                            className="mr-3"
                          />
                          <Label
                            htmlFor={`searchType-${option.type}`}
                            className="text-sm text-gray-700 cursor-pointer flex-1"
                          >
                            {option.name}
                          </Label>
                        </div>
                      ))}
                    </div>
                  </div>
                </PopoverContent>
              </Popover>
            </div>
          </div>

          {searchType === "location" && (
            <div className="min-w-[320px] relative overflow-visible">
              <SearchAutocomplete
                isLoading={isLoadingSpaces}
                options={spaces.map((space) => ({
                  value: space.placeId,
                  label: space.name,
                  description: space.address,
                  icon: <MapPinIcon className="w-4" />,
                  data: space,
                }))}
                placeholder="Busque por endereço, espaço, referência ou sala privativa"
                value={filters.location || ""}
                onInputChange={(value) => {
                  setSearchSelectedValue(value);
                  handleSearchSpaces(value);
                }}
                onValueChange={(_, data) => {
                  handleSearchLocation(data);
                }}
              />
            </div>
          )}

          {searchType === "space" && (
            <div className="min-w-[320px] relative overflow-visible">
              <SearchInputCommand
                name="spaces"
                selectOption={(_, item) => {
                  clearIrrelevantLocationFields("space");
                  setSelectedSpace(item);
                  setFilters((prev) => ({ ...prev, spaceId: item.id }));
                  handleSearchAndTrack({
                    ...filters,
                    spaceId: item.id,
                  });
                }}
                searchValue={selectedSpace?.name || ""}
                defaultValue={selectedSpace?.name || ""}
                optionsList={filterSpaces || []}
                updateList={(term: string) =>
                  handSelectedSpace(term, [SPACE_TYPE.COWORKING])
                }
                label="Busque por nome do espaço"
                inputWidth="w-full"
              />
            </div>
          )}

          {searchType === "privateOfficeIds" && (
            <div className="min-w-[320px] relative overflow-visible">
              <Input
                placeholder="Digite o ID da sala"
                value={roomId}
                onChange={handleChangeRoomId}
                className="w-full"
              />
            </div>
          )}

          {searchType === "neighborhoods" && (
            <div className="flex gap-2 items-center overflow-visible">
              <div className="min-w-[320px] relative overflow-visible">
                <SearchInputCommand
                  name="cities"
                  selectOption={(_, item) => handleCitySelect(item)}
                  searchValue={filters.cityName || selectedCity?.name || ""}
                  defaultValue={filters.cityName || selectedCity?.name || ""}
                  optionsList={filterCities || []}
                  updateList={handSelectedCity}
                  label="Cidade"
                  inputWidth="w-full"
                />
              </div>
              <div className="min-w-[320px] w-[320px] relative overflow-visible">
                <SearchInputCommand
                  name="neighborhood"
                  chevron
                  selectOption={(_, item) => handleNeighborhoodSelect(item)}
                  selectedList={
                    filters.neighborhood
                      ? filters.neighborhood.map((name, index) => ({
                          id: index,
                          name,
                        }))
                      : selectedNeighborhoods
                  }
                  searchValue=""
                  optionsList={filterNeighborhoods || []}
                  updateList={(term: string) =>
                    handSelectedNeighborhood(term, selectedCity?.name || "")
                  }
                  label="Bairros"
                  inputWidth="w-full"
                  disabled={!selectedCity}
                />
              </div>
            </div>
          )}

          {!HIDE_BTS_ITEMS && (
            <>
              <div
                onClick={toggleFurnished}
                className="flex items-center justify-center space-x-2 px-3 h-10 bg-white border rounded-full text-sm hover:border-gray-500 transition-all duration-300 cursor-pointer hover:border-gray-500"
                role="button"
                tabIndex={0}
                aria-pressed={filters.isFurnished}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    toggleFurnished();
                  }
                }}
              >
                <span className="text-sm text-gray-700">Mobiliado</span>
                <div className="flex items-center">
                  <div
                    className={`relative inline-flex h-5 w-9 flex-shrink-0 items-center rounded-full transition-colors focus:outline-none ${
                      filters.isFurnished ? "bg-gray-600" : "bg-gray-200"
                    }`}
                    aria-hidden="true"
                  >
                    <span
                      className={`${
                        filters.isFurnished ? "translate-x-5" : "translate-x-1"
                      } inline-block h-3 w-3 transform rounded-full bg-white transition-transform`}
                    />
                  </div>
                </div>
              </div>

              <Popover>
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    className={`flex items-center justify-center space-x-2 px-3 h-10 bg-white border rounded-full text-sm hover:border-gray-500 transition-all duration-300 ${
                      hasBudgetFilter ? "border-gray-500" : "border-gray-300"
                    }`}
                    aria-label="Budget range filter"
                  >
                    <DollarSign
                      className="h-4 w-4 text-gray-600"
                      aria-hidden="true"
                    />
                    <span className="text-gray-700 truncate">
                      {getBudgetRangeText()}
                    </span>
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-80 bg-white shadow-lg rounded-lg p-4 border border-gray-200 z-[9999]">
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <h3 className="font-medium text-gray-900">Budget</h3>
                      {hasBudgetFilter && (
                        <button
                          type="button"
                          onClick={clearBudget}
                          className="text-gray-400 hover:text-gray-600 transition-colors p-1"
                          aria-label="Clear budget filter"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                    <div className="flex flex-col space-y-3">
                      <div className="relative w-full">
                        <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-gray-500">
                          R$
                        </span>
                        <Input
                          type="number"
                          value={filters.budgetMin}
                          onChange={(e) =>
                            updateFilter(
                              "budgetMin",
                              e.target.value === ""
                                ? ""
                                : Number(e.target.value),
                            )
                          }
                          placeholder="Valor mínimo"
                          className="w-full pl-12 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-transparent transition-all duration-300"
                          aria-label="Minimum budget"
                        />
                      </div>
                      <div className="relative w-full">
                        <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-gray-500">
                          R$
                        </span>
                        <Input
                          type="number"
                          value={filters.budgetMax}
                          onChange={(e) =>
                            updateFilter(
                              "budgetMax",
                              e.target.value === ""
                                ? ""
                                : Number(e.target.value),
                            )
                          }
                          placeholder="Valor máximo"
                          className="w-full pl-12 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-transparent transition-all duration-300"
                          aria-label="Maximum budget"
                        />
                      </div>
                    </div>
                  </div>
                </PopoverContent>
              </Popover>

              <Popover>
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    className={`flex items-center justify-center space-x-2 px-3 h-10 bg-white border rounded-full text-sm hover:border-gray-500 transition-all duration-300 ${
                      hasSizeFilter ? "border-gray-500" : "border-gray-300"
                    }`}
                    aria-label="Size range filter"
                  >
                    <Maximize2
                      className="h-4 w-4 text-gray-600"
                      aria-hidden="true"
                    />
                    <span className="text-gray-700 truncate">
                      {getSizeRangeText()}
                    </span>
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-80 bg-white shadow-lg rounded-lg p-4 border border-gray-200 z-[9999]">
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <h3 className="font-medium text-gray-900">Tamanho</h3>
                      {hasSizeFilter && (
                        <button
                          type="button"
                          onClick={clearSize}
                          className="text-gray-400 hover:text-gray-600 transition-colors p-1"
                          aria-label="Clear size filter"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                    <div className="flex flex-col space-y-3">
                      <div className="relative w-full">
                        <Input
                          type="number"
                          value={filters.sizeMin}
                          onChange={(e) =>
                            updateFilter(
                              "sizeMin",
                              e.target.value === ""
                                ? ""
                                : Number(e.target.value),
                            )
                          }
                          placeholder="Tamanho mínimo"
                          className="w-full pl-3 pr-8 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-transparent transition-all duration-300"
                          aria-label="Minimum size"
                        />
                        <span className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-500">
                          m²
                        </span>
                      </div>
                      <div className="relative w-full">
                        <Input
                          type="number"
                          value={filters.sizeMax}
                          onChange={(e) =>
                            updateFilter(
                              "sizeMax",
                              e.target.value === ""
                                ? ""
                                : Number(e.target.value),
                            )
                          }
                          placeholder="Tamanho máximo"
                          className="w-full pl-3 pr-8 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-transparent transition-all duration-300"
                          aria-label="Maximum size"
                        />
                        <span className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-500">
                          m²
                        </span>
                      </div>
                    </div>
                  </div>
                </PopoverContent>
              </Popover>

              <Popover>
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    className={`flex items-center justify-center space-x-2 px-3 h-10 bg-white border rounded-full text-sm hover:border-gray-500 transition-all duration-300 ${
                      hasPositionsFilter ? "border-gray-500" : "border-gray-300"
                    }`}
                    aria-label="Positions range filter"
                  >
                    <Users
                      className="h-4 w-4 text-gray-600"
                      aria-hidden="true"
                    />
                    <span className="text-gray-700 truncate">
                      {getPositionsRangeText()}
                    </span>
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-80 bg-white shadow-lg rounded-lg p-4 border border-gray-200 z-[9999]">
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <h3 className="font-medium text-gray-900">Posições</h3>
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Info className="h-4 w-4 text-gray-400 hover:text-gray-600 cursor-help" />
                            </TooltipTrigger>
                            <TooltipContent>
                              <p>Consideram-se 5m² por posição.</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </div>
                      {hasPositionsFilter && (
                        <button
                          type="button"
                          onClick={clearPositions}
                          className="text-gray-400 hover:text-gray-600 transition-colors p-1"
                          aria-label="Clear positions filter"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                    <div className="flex flex-col space-y-3">
                      <div className="relative w-full">
                        <Input
                          type="number"
                          min={0}
                          value={filters.positionsMin}
                          onChange={(e) =>
                            updateFilter(
                              "positionsMin",
                              e.target.value === ""
                                ? ""
                                : Number(e.target.value),
                            )
                          }
                          placeholder="Posições mínimas"
                          className="w-full pl-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-transparent transition-all duration-300"
                          aria-label="Minimum positions"
                        />
                      </div>
                      <div className="relative w-full">
                        <Input
                          type="number"
                          min={0 || filters.positionsMin}
                          value={filters.positionsMax}
                          onChange={(e) =>
                            updateFilter(
                              "positionsMax",
                              e.target.value === ""
                                ? ""
                                : Number(e.target.value),
                            )
                          }
                          placeholder="Posições máximas"
                          className="w-full pl-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-transparent transition-all duration-300"
                          aria-label="Maximum positions"
                        />
                      </div>
                    </div>
                  </div>
                </PopoverContent>
              </Popover>
            </>
          )}

          <button
            type="button"
            onClick={() => setIsDrawerOpen(true)}
            className={`flex items-center justify-center space-x-2 px-3 h-10 bg-white border rounded-full text-sm hover:border-gray-500 transition-all duration-300 relative ${
              hasPainelFilters() ? "border-gray-500" : "border-gray-300"
            }`}
            aria-label="More filters"
          >
            <Filter className="h-4 w-4 text-gray-600" aria-hidden="true" />
            <span className="text-gray-700">Mais filtros</span>
            {hasPainelFilters() && (
              <div className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full border-2 border-white"></div>
            )}
          </button>

          {HIDE_SEMANTIC_SEARCH && (
            <Button
              type="submit"
              size="sm"
              className="h-10 ml-auto"
              aria-label="Submit search"
            >
              <MagnifyingGlassIcon className="w-4 mr-2" />
              Buscar
            </Button>
          )}

          <PainelFiltersList
            isOpen={isDrawerOpen}
            onClose={() => setIsDrawerOpen(false)}
            onSearchSubmit={(updatedFilters: PainelFilters) => {
              const finalFilters = { ...filters, ...updatedFilters };
              handleSearchAndTrack(finalFilters);
              setIsDrawerOpen(false);
            }}
            onFiltersChange={updatePainelFilters}
            filters={painelFilters}
            dealId={dealId ?? null}
          />
        </div>
      </form>
    </div>
  );
}
